import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { MED_NECESSITY_CONTRACT_TEXT, CONTRACT_VERSION } from '../../constants/contract';
import api from '../../utils/api';
import './ContractAgreement.css';

/* ═══════════════════════════ ICONS ═══════════════════════════ */
const SealSvg = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 1.5L13 4v4c0 3-2.5 5.5-5 6.5-2.5-1-5-3.5-5-6.5V4z" />
    <path d="M5.5 8l2 2L11 6.5" />
  </svg>
);
const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
    <circle cx="8" cy="8" r="3" />
    <path d="M8 1.5v1M8 13.5v1M1.5 8h1M13.5 8h1M3.4 3.4l.7.7M11.9 11.9l.7.7M3.4 12.6l.7-.7M11.9 4.1l.7-.7" />
  </svg>
);
const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
    <path d="M13 9.5a5.5 5.5 0 01-6-6A5.5 5.5 0 1013 9.5z" />
  </svg>
);
const WarnIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 2L1.5 13h13L8 2zM8 6.5v3M8 11.5v.01" />
  </svg>
);
const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
  </svg>
);
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8.4l3 3 7-7" />
  </svg>
);
const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="8" r="6" />
    <path d="M5 8.2l2.2 2.2L11 6.6" />
  </svg>
);
const LogoutIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 4V2.5h-6V13.5h6V12" />
    <path d="M6.5 8H14M11.5 5.5L14 8l-2.5 2.5" />
  </svg>
);
const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

/* ═══════════════════════════ FONT LOADER ═══════════════════════════ */
const useContractFonts = () => {
  useEffect(() => {
    const links: HTMLLinkElement[] = [];
    const add = (rel: string, href: string, crossOrigin?: string) => {
      const l = document.createElement('link');
      l.rel = rel; l.href = href;
      if (crossOrigin) l.crossOrigin = crossOrigin;
      document.head.appendChild(l); links.push(l);
    };
    add('preconnect', 'https://fonts.googleapis.com');
    add('preconnect', 'https://fonts.gstatic.com', 'anonymous');
    add(
      'stylesheet',
      'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..700;1,9..144,400..600&family=Geist:wght@300..700&family=Geist+Mono:wght@400..500&display=swap'
    );
    return () => { links.forEach((l) => document.head.removeChild(l)); };
  }, []);
};

/* ═══════════════════════════ COMPONENT ═══════════════════════════ */
interface ContractAgreementProps {
  onAccept: () => void;
  onLogout: () => void;
  userEmail?: string;
}

const ContractAgreement: React.FC<ContractAgreementProps> = ({ onAccept, onLogout, userEmail }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  useContractFonts();

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [showScrollAlert, setShowScrollAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const contractParagraphs = useMemo(
    () => MED_NECESSITY_CONTRACT_TEXT.split('\n\n'),
    []
  );

  // Detect if the document is short enough that no scrolling is needed
  useEffect(() => {
    const checkScrollable = () => {
      const node = contentRef.current;
      if (!node) return;
      // If the body fits without scrolling, treat as already reviewed.
      if (node.scrollHeight - node.clientHeight < 8) {
        setHasScrolledToEnd(true);
      }
    };
    // Run on mount and on resize
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, []);

  const handleScroll = () => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const reachedBottom = scrollTop + clientHeight >= scrollHeight - 24;
    if (reachedBottom && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
      setShowScrollAlert(false);
    }
  };

  const handleAccept = async () => {
    if (!hasScrolledToEnd) {
      setShowScrollAlert(true);
      return;
    }
    const trimmedSignature = signatureName.trim();
    if (!trimmedSignature || trimmedSignature.length < 2) {
      setSubmitError('Please enter your full legal name (at least 2 characters).');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await api.post('/api/contract/sign', {
        signatureName: trimmedSignature,
        contractVersion: CONTRACT_VERSION,
      });

      if (response.data.success) {
        onAccept();
      } else {
        setSubmitError(response.data.error || 'Failed to sign contract. Please try again.');
        setIsSubmitting(false);
      }
    } catch (err: any) {
      console.error('Error signing contract:', err);
      if (err.response?.status === 409) {
        setSubmitError('You have already signed this contract. Redirecting...');
        setTimeout(() => onAccept(), 1500);
      } else {
        setSubmitError(err.response?.data?.error || 'An error occurred while signing. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  const readyToAccept =
    hasScrolledToEnd && acknowledged && signatureName.trim().length > 1 && !isSubmitting;

  return (
    <div className={`contract-page${isDarkMode ? ' dark' : ''}`}>
      {/* Theme toggle */}
      <div className="contract-corner">
        <button
          type="button"
          className="contract-ctrl"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {isDarkMode ? <SunIcon /> : <MoonIcon />}
        </button>
      </div>

      <motion.div
        className="contract-card"
        initial={{ opacity: 0, y: 16, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 26 }}
      >
        {/* HEADER */}
        <header className="contract-head">
          <div className="contract-head-top">
            <div className="contract-head-text">
              <div className="contract-eyebrow">
                <span className="contract-eyebrow-rule" aria-hidden />
                <em>required before access</em>
              </div>
              <h1 className="contract-h1">
                Terms <em>&amp; Conditions.</em>
              </h1>
              <p className="contract-sub">
                Every user signs the latest agreement before reaching the platform.
                Read in full, acknowledge, and provide an electronic signature to continue.
              </p>
            </div>

            {userEmail && (
              <div className="contract-user">
                <span className="contract-user-key">Signed in as</span>
                <span className="contract-user-val">
                  <SealSvg />
                  {userEmail}
                </span>
              </div>
            )}
          </div>

        </header>

        {/* BANNERS */}
        {showScrollAlert && !hasScrolledToEnd && (
          <div className="contract-banner is-warn" role="alert">
            <WarnIcon />
            <span>
              <span className="contract-banner-key">Notice /</span>
              Please review the entire agreement by scrolling to the bottom before signing.
            </span>
          </div>
        )}

        {submitError && (
          <div className="contract-banner is-error" role="alert">
            <WarnIcon />
            <span>
              <span className="contract-banner-key">Error /</span>
              {submitError}
            </span>
            <button
              type="button"
              className="contract-banner-dismiss"
              onClick={() => setSubmitError(null)}
              aria-label="Dismiss"
            >
              <CloseIcon />
            </button>
          </div>
        )}

        {/* BODY */}
        <div
          ref={contentRef}
          className="contract-body"
          onScroll={handleScroll}
        >
          <div className="contract-body-strip">
            <span className="contract-body-strip-rule" aria-hidden />
            <span>Begin agreement</span>
            <span className="contract-body-strip-rule" aria-hidden />
          </div>
          {contractParagraphs.map((paragraph, index) => (
            <p key={index} className="contract-paragraph">
              {paragraph}
            </p>
          ))}
        </div>

        {/* FOOTER */}
        <div className="contract-foot">
          <label className="contract-acknowledge">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span className="contract-acknowledge-box" aria-hidden>
              {acknowledged && <CheckIcon />}
            </span>
            <span>
              I acknowledge that I have read, understand, and agree to the
              {' '}<strong style={{ color: 'var(--ct-ink)', fontWeight: 500 }}>Med Necessity Terms &amp; Conditions</strong>.
            </span>
          </label>

          <div className="contract-signature">
            <label className="contract-signature-label" htmlFor="contract-signature-input">
              Full legal name
              <span className="contract-signature-label-required">*</span>
            </label>
            <input
              id="contract-signature-input"
              className="contract-signature-input"
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Type your full legal name"
              maxLength={120}
              autoComplete="name"
            />
            <span className="contract-signature-hint">
              Typing your name constitutes your electronic signature.
            </span>
          </div>

          <div className="contract-actions">
            <span className={`contract-progress${hasScrolledToEnd ? ' is-done' : ''}`}>
              <CheckCircleIcon />
              {hasScrolledToEnd
                ? 'Contract reviewed in full'
                : 'Scroll to the bottom to confirm review'}
            </span>

            <div className="contract-actions-buttons">
              <button
                type="button"
                className="contract-btn contract-btn-ghost"
                onClick={onLogout}
                disabled={isSubmitting}
              >
                <LogoutIcon />
                <span>Sign out</span>
              </button>
              <button
                type="button"
                className="contract-btn contract-btn-primary"
                onClick={handleAccept}
                disabled={!readyToAccept}
              >
                {isSubmitting ? (
                  <>
                    <span className="contract-spinner" />
                    <span>Signing&hellip;</span>
                  </>
                ) : (
                  <>
                    <CheckIcon />
                    <span>I Agree &amp; Sign</span>
                    <ArrowRight />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ContractAgreement;
